import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Card,
  Modal,
  TextField,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "../../utils/axiosConfig";
import { fadeInUp, fadeInDown, modalScaleIn } from "../../utils/animations";

const API_BASE_URL = "/api/institutions";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: "90vw", md: 500 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  maxHeight: "90vh",
  overflowY: "auto",
};

// --- SOUS-COMPOSANTS POUR LES CELLULES ---

const StatutCell = ({ value }) => (
  <MDBadge
    badgeContent={value ? "Actif" : "Inactif"}
    color={value ? "success" : "secondary"}
    variant="gradient"
    size="sm"
  />
);

StatutCell.propTypes = {
  value: PropTypes.bool.isRequired,
};

const CellWithActions = ({ row, onEdit, onToggle, onDelete }) => (
  <MDBox display="flex" justifyContent="center">
    <MDButton color="info" size="small" onClick={() => onEdit(row.original)} sx={{ mr: 1 }}>
      Modifier
    </MDButton>
    <MDButton
      color={row.original.actif ? "warning" : "success"}
      size="small"
      onClick={() => onToggle(row.original.id)}
      sx={{ mr: 1 }}
    >
      {row.original.actif ? "Désactiver" : "Activer"}
    </MDButton>
    <MDButton color="error" size="small" onClick={() => onDelete(row.original.id)}>
      Supprimer
    </MDButton>
  </MDBox>
);

CellWithActions.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      nom: PropTypes.string,
      description: PropTypes.string,
      actif: PropTypes.bool,
    }).isRequired,
  }).isRequired,
};

// --- COMPOSANT PRINCIPAL ---

function Institutions() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    nom: "",
    description: "",
    actif: true,
    admin_nom: "",
    admin_email: "",
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: "", onConfirm: null });
  const openConfirm = (message, onConfirm) => setConfirmDialog({ open: true, message, onConfirm });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(API_BASE_URL + "/");
      setRows(response.data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Erreur de chargement des institutions.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpen = () => {
    setIsEdit(false);
    setFormData({
      id: null,
      nom: "",
      description: "",
      actif: true,
      admin_nom: "",
      admin_email: "",
    });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleEdit = useCallback((rowData) => {
    setIsEdit(true);
    setFormData(rowData);
    setOpen(true);
  }, []);

  const handleToggle = useCallback(
    async (id) => {
      try {
        await api.patch(`${API_BASE_URL}/${id}/toggle-actif`);
        setSnackbar({ open: true, message: "Statut mis à jour !", severity: "success" });
        fetchData();
      } catch (error) {
        const errorMsg = error.response?.data?.detail || "Erreur lors du changement de statut.";
        setSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    },
    [fetchData]
  );

  const handleDelete = useCallback(
    (id) => {
      openConfirm("Voulez-vous vraiment supprimer cette institution ?", async () => {
        try {
          await api.delete(`${API_BASE_URL}/${id}`);
          setSnackbar({ open: true, message: "Institution supprimée !", severity: "success" });
          fetchData();
        } catch (error) {
          const errorMsg = error.response?.data?.detail || "Erreur lors de la suppression.";
          setSnackbar({ open: true, message: errorMsg, severity: "error" });
        }
      });
    },
    [fetchData]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (isEdit) {
        const { admin_nom, admin_email, ...updateData } = formData;
        await api.put(`${API_BASE_URL}/${formData.id}`, updateData);
        setSnackbar({ open: true, message: "Institution mise à jour !", severity: "success" });
      } else {
        await api.post(API_BASE_URL + "/", formData);
        setSnackbar({
          open: true,
          message: "Institution créée ! Les identifiants ont été envoyés par email.",
          severity: "success",
        });
      }
      handleClose();
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'enregistrement.";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Institution",
        accessor: "nom",
        align: "left",
        width: "50%",
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          <MDBox>
            {/* eslint-disable-next-line react/prop-types */}
            <MDTypography variant="button" fontWeight="medium" display="block">
              {/* eslint-disable-next-line react/prop-types */}
              {row.original.nom}
            </MDTypography>
            {/* eslint-disable-next-line react/prop-types */}
            {row.original.description && (
              <MDTypography variant="caption" color="text" display="block">
                {/* eslint-disable-next-line react/prop-types */}
                {row.original.description}
              </MDTypography>
            )}
          </MDBox>
        ),
      },
      {
        Header: "Statut",
        accessor: "actif",
        align: "center",
        Cell: StatutCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          <CellWithActions
            row={row}
            onEdit={handleEdit}
            onToggle={handleToggle}
            onDelete={handleDelete}
          />
        ),
      },
    ],
    [handleEdit, handleToggle, handleDelete]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="purple"
                borderRadius="lg"
                coloredShadow="dark"
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
              >
                <MDBox>
                  <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
                    Gestion des Institutions
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.72)", display: "block", mt: 0.3 }}
                  >
                    Gestion des institutions financières enregistrées
                  </MDTypography>
                </MDBox>
                <MDButton variant="outlined" color="white" onClick={handleOpen}>
                  Nouvelle Institution
                </MDButton>
              </MDBox>
              <MDBox px={3} pt={3} pb={1}>
                <TextField
                  size="small"
                  placeholder="Rechercher par nom ou description…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: 300 }}
                />
              </MDBox>
              <MDBox pt={1}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{
                      columns,
                      rows: rows.filter((r) => {
                        const q = search.toLowerCase();
                        return (
                          r.nom?.toLowerCase().includes(q) ||
                          r.description?.toLowerCase().includes(q)
                        );
                      }),
                    }}
                    isSorted
                    entriesPerPage
                    showTotalEntries
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Modal open={open} onClose={handleClose}>
        <Box sx={{ ...modalStyle, animation: `${modalScaleIn} 0.3s ease-out both` }}>
          <MDTypography variant="h6" mb={3}>
            {isEdit ? "Modifier Institution" : "Nouvelle Institution"}
          </MDTypography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Nom de l'Institution"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={3}
            />
            {!isEdit && (
              <>
                <TextField
                  fullWidth
                  label="Nom de l'administrateur"
                  name="admin_nom"
                  value={formData.admin_nom}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Email de l'administrateur"
                  name="admin_email"
                  type="email"
                  value={formData.admin_email}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.actif}
                  onChange={handleChange}
                  name="actif"
                  color="info"
                />
              }
              label="Institution Active"
              sx={{ mt: 2 }}
            />
            <MDBox mt={4} display="flex" justifyContent="flex-end">
              <MDButton variant="text" color="secondary" onClick={handleClose} sx={{ mr: 1 }}>
                Annuler
              </MDButton>
              <MDButton type="submit" variant="gradient" color="purple" disabled={isSubmitting}>
                {isSubmitting ? "Envoi..." : "Enregistrer"}
              </MDButton>
            </MDBox>
          </form>
        </Box>
      </Modal>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="secondary"
          >
            Annuler
          </MDButton>
          <MDButton
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
            variant="gradient"
            color="warning"
          >
            Confirmer
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default Institutions;
